import {RFValue} from 'react-native-responsive-fontsize';
import {colors} from './colors';
import {
  SCREEN_HEIGHT_REFERENCE,
  Theme,
  getFontConfig,
  getFontSize,
  WEB_FONT_SIZES,
} from './lightTheme';
import {Platform} from 'react-native';

/**
 * Represents the dark theme object.
 * This can be used to create different themes like dark and light.
 * It can be then connected to React context and controlled via a setting flag.
 * @type {object}
 */
const darkTheme: Theme = {
  primary: '#6633FF', // Keep vibrant purple/blue
  secondary: '#5A20CB', // Darker purple
  tertiary: '#4BB2C0',
  ripple: '#444',
  overlay: 'rgba(0,0,0,0.6)',
  headerBackground: '#1A1A1A', // Dark header
  homeBackground: '#0E0F64', // Dark blue background
  screenBackground: '#1A1A1A', // Dark screen background
  cardBackground: '#2A2A2A', // Dark card background
  cardBackgroundEnd: 'rgba(30,30,50,0.9)',
  icon: '#6633FF', // Purple/blue icons
  skeletonBackground: '#333333',
  skeletonShimmer: '#0E0F64',
  border: '#333333',
  primaryOverlay: 'rgba(40, 35, 245, 0.3)',
  backgroundOverlay: 'rgba(0,0,0,0.2)',
  imageBackground: '#1A1A1A',
  alert: {
    successBackgroundPrimary: '#0C7040',
    errorBackgroundPrimary: '#C72C41',
    warningBackgroundPrimary: '#FC8621',
    helpBackgroundPrimary: '#0070E0',
    successBackgroundSecondary: '#004E32',
    errorBackgroundSecondary: '#801336',
    warningBackgroundSecondary: '#C24914',
    helpBackgroundSecondary: '#05478A',
    textPrimary: '#FFFFFF',
  },
  fonts: getFontConfig(),
  text: {
    dark: '#f5f5f5',
    black: '#FFFFFF',
    border: '#555555',
    icon: '#A1A1A1',
    error: '#E57373',
    success: '#4CAF50',
    grey: '#CCCCCC',
    greyLight: '#999999',
    title: '#FFFFFF',
    description: '#B0B0B0',
    light: '#E0E0E0',
    s1: getFontSize(WEB_FONT_SIZES.s1),
    s2: getFontSize(WEB_FONT_SIZES.s2),
    s3: getFontSize(WEB_FONT_SIZES.s3),
    s4: getFontSize(WEB_FONT_SIZES.s4),
    s5: getFontSize(WEB_FONT_SIZES.s5),
    s55: getFontSize(WEB_FONT_SIZES.s55),
    s6: getFontSize(WEB_FONT_SIZES.s6),
    s7: getFontSize(WEB_FONT_SIZES.s7),
    s8: getFontSize(WEB_FONT_SIZES.s8),
    s85: getFontSize(WEB_FONT_SIZES.s85),
    s89: getFontSize(WEB_FONT_SIZES.s89),
    s9: getFontSize(WEB_FONT_SIZES.s9),
    s10: getFontSize(WEB_FONT_SIZES.s10),
    s11: getFontSize(WEB_FONT_SIZES.s11),
    s12: getFontSize(WEB_FONT_SIZES.s12),
  },
  button: {
    text: '#FFFFFF',
    lightColor: colors.primary,
    outline: colors.secondary,
  },
  textInput: {
    text: '#070A7A',
    cursorColor: colors.primary,
    placeholder: '#BBBBBB',
    border: '#555555',
    required: '#FF8A80',
    background: '#f8f8f8',
  },
  fab: {
    backgroundColor: '#2A2A2A',
  },
  header: {
    primary: '#FFFFFF',
    background: '#070A7A',
    startLinearGradient: 'rgba(11,13,111,0.8)',
    endLinearGradient: 'rgba(13,15,138,0.8)',
  },
  bottomTabs: {
    background: 'rgba(68,70,151,0.95)',
    activeTabBackground: colors.primary + 'AA',
    label: '#FFFFFF',
    icon: '#FFFFFF',
    iconFocus: '#FFFFFF',
  },
  activityListItem: {
    startColor: '#2E2E2E',
    endColor: '#3A3A3A',
  },
  switch: {
    trackOff: '#C8D8FF',
    trackOn: '#C8D8FF',
    thumbOff: '#A3BEFF',
    thumbOn: '#2D27F6',
  },
  search: {
    placeholder: '#CCCCCC',
  },
  questionInput: {
    text: '#FFFFFF',
    placeholder: '#AAAAAA',
    startColor: 'rgba(40, 35, 245, 0.5)',
    endColor: 'rgba(11, 13, 111, 0.3)',
  },
  voiceQuestionInput: {
    text: '#FFFFFF',
    background: '#333333',
    inputPlaceholder: 'rgba(255,255,255,0.3)',
    iosEndColor: 'rgba(255,255,255,0)',
    mainButtonStartColor: '#6633FF',
    mainButtonEndColor: '#5A20CB',
    bubbleBackground: '#2A2A2A', // Dark bubble background
  },
  afterLogin: {
    contentBackground: '#1A1A1A',
    text: '#E0E0E0',
    label: '#CCCCCC',
    line: '#666666',
    selected: 'rgba(255,255,255,0.1)',
    checkIcon: '#555555',
    checkIconBackground: colors.primary,
  },
  chatBubble: {
    backgroundSend: '#3C3D8C',
    backgroundReceived: '#3C3D8C',
    selected: 'rgba(40, 35, 245, 0.2)',
    date: '#BBBBBB',
    error: '#FF8A80',
    favorite: '#FFD707',
    sendText: '#FFFFFF',
    receivedText: '#E0E0E0',
    online: '#52BD57',
    offline: '#9E9E9E',
  },
  onboarding: {
    text: '#FFFFFF',
    background: '#0B0D6F',
    textSecondary: colors.tertiary,
  },
  home: {
    cardBackground: '#3C3D8C',
  },
  menu: {
    nav: '#f5f5f5',
  },
  fontChange: {
    card: '#464BCF',
  },
  settings: {
    iconBackground: '#EEF0FF',
    icon: '#EEF0FF',
  },
  quickAccess: {
    background: '#444697',
    iconBackground: '#0B0D6F',
  },
  chat: {
    startLinearGradient: '#005E66',
    endLinearGradient: '#3A33A3',
  },
  tabNews: {
    startLinearGradient: '#1d4d4a',
    secondLinearGradient: '#2c3d99',
    thirdLinearGradient: '#230066',
    button: '#146c8a',
    cardBackground: '#3A4A6B',
  },
  drugsDiseases: {
    tabBackground: '#1C86A8',
    gradientTopLeft: '#4ABFC1',
    gradientMidTop: '#4B6BC0',
    gradientCenter: '#464BCF',
    gradientBottomRight: '#2C0099',
  },
  questionModal: {
    title: '#dfdfff',
  },
  patients: {
    headerBackground: 'rgba(18,18,39,0.9)',
  },
  // Additional theme properties for enhanced chat functionality
  isMobile: false, // Will be set dynamically
  shadow: 'rgba(0,0,0,0.3)',
  optionIcon: '#CCCCCC',
  shine: '#6633FF',
  categoryBullet: {
    background: '#333333',
    selectedBackground: '#6633FF',
    text: '#E0E0E0',
    selectedText: '#FFFFFF',
    selectedBorder: '#6633FF', // Fixed property name
    hoverBackground: '#444444',
    selectedHoverBackground: '#5A20CB',
  },
  dropdown: {
    background: '#2A2A2A',
    text: '#E0E0E0',
    delete: '#E57373',
  },
  table: {
    thead: '#333333',
    th: '#E0E0E0',
    evenRow: '#2A2A2A',
    trHover: '#444444',
    text: '#E0E0E0',
  },
  scrollbar: {
    track: '#333333',
    thumb: '#666666',
    thumbHover: '#888888',
  },
};

export default darkTheme;
